/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { RegisterTaskDataMultipleCharge, TasksApi } from "client";
import { Const } from "const";
import { useApiWithTimeout } from "hooks/ApiWrapperWithTimeout";
import { useRegisteredMultipleChargeTasks } from "components/TaskRegisterMultipleCharge/hooks/GetMultipleChargeTasksHook";
import { useWaitTaskStatus } from "hooks/WaitTaskStatusHook";
import { useCallback, useState } from "react";
import { useCallApi } from "hooks/CallApiHook";

export const useRegisterMultipleChargeTask = () => {
  const [registerTask, registerTaskCanceler] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.registerTask
  );
  const [request, setRequest] = useState<RegisterTaskDataMultipleCharge>();
  const { getMultipleChargeTasks, canceler: getMultipleChargeTasksCanceler } =
    useRegisteredMultipleChargeTasks();
  const waitTaskStatus = useWaitTaskStatus();

  const cancelCallback = useCallback(() => {
    registerTaskCanceler();
    getMultipleChargeTasksCanceler();
  }, [registerTaskCanceler, getMultipleChargeTasksCanceler]);

  const result = useCallApi(
    async () => {
      if (request !== undefined) {
        // Register jobset
        const response = await registerTask.createTaskMultipleCharge({
          registerTaskDataMultipleCharge: request,
        });

        // Wait for task creation
        await waitTaskStatus(
          response.jobSetId,
          [Const.taskStatus.notApproved],
          Const.operationTimeout.getTaskCreationProgress
        );

        // Get result
        const jobset = await getMultipleChargeTasks(response.jobSetId);
        if (jobset === undefined) {
          throw new Error("Registered jobset not found");
        }
        return jobset;
      }
    },
    cancelCallback,
    [
      request,
      cancelCallback,
      registerTask,
      getMultipleChargeTasks,
      waitTaskStatus,
    ]
  );

  return { ...result, setRequest };
};
